import { useEffect, useState, useCallback } from 'react';
import { useAdminStore } from '../store/adminStore';
import {
  Bell,
  X,
  CheckCircle,
  AlertTriangle,
  Info,
  ShoppingCart,
  Package,
  Trash2,
  Clock,
} from 'lucide-react';

export interface Notification {
  id: string;
  type: 'success' | 'warning' | 'info' | 'error';
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  icon?: 'order' | 'product' | 'delete' | 'status' | 'system';
}

// Toast component
function Toast({ notification, onRemove }: { notification: Notification; onRemove: (id: string) => void }) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onRemove(notification.id), 300);
    }, 4000);
    return () => clearTimeout(timer);
  }, [notification.id, onRemove]);

  const bgColors = {
    success: 'bg-green-50 border-green-200',
    warning: 'bg-yellow-50 border-yellow-200',
    error: 'bg-red-50 border-red-200',
    info: 'bg-blue-50 border-blue-200',
  };

  const iconColors = {
    success: 'text-green-500',
    warning: 'text-yellow-500',
    error: 'text-red-500',
    info: 'text-blue-500',
  };

  const icons = {
    success: <CheckCircle className={`w-5 h-5 ${iconColors.success}`} />,
    warning: <AlertTriangle className={`w-5 h-5 ${iconColors.warning}`} />,
    error: <Trash2 className={`w-5 h-5 ${iconColors.error}`} />,
    info: <Info className={`w-5 h-5 ${iconColors.info}`} />,
  };

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-xl border shadow-lg transition-all duration-300 ${
        bgColors[notification.type]
      } ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}`}
    >
      <div className="flex-shrink-0 mt-0.5">
        {icons[notification.type]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">{notification.title}</p>
        <p className="text-xs text-gray-600 mt-0.5">{notification.message}</p>
      </div>
      <button
        onClick={() => {
          setIsExiting(true);
          setTimeout(() => onRemove(notification.id), 300);
        }}
        className="flex-shrink-0 p-1 hover:bg-black/5 rounded-lg transition-colors"
      >
        <X className="w-4 h-4 text-gray-400" />
      </button>
    </div>
  );
}

// Toast Container
export function ToastContainer() {
  const { notifications, removeNotification } = useAdminStore();
  const toasts = notifications.filter((n) => Date.now() - n.timestamp < 5000 && !n.read);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] space-y-2 max-w-sm">
      {toasts.slice(0, 3).map((toast) => (
        <Toast key={toast.id} notification={toast} onRemove={removeNotification} />
      ))}
    </div>
  );
}

// Notification Panel (dropdown)
export function NotificationPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { notifications, markNotificationRead, markAllNotificationsRead, removeNotification } = useAdminStore();

  const sortedNotifications = [...notifications].sort((a, b) => b.timestamp - a.timestamp);
  const unreadCount = notifications.filter((n) => !n.read).length;

  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Az önce';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} dk önce`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} saat önce`;
    const days = Math.floor(hours / 24);
    return `${days} gün önce`;
  };

  const getIcon = (notification: Notification) => {
    const iconMap = {
      order: <ShoppingCart className="w-4 h-4 text-blue-500" />,
      product: <Package className="w-4 h-4 text-green-500" />,
      delete: <Trash2 className="w-4 h-4 text-red-500" />,
      status: <Clock className="w-4 h-4 text-purple-500" />,
      system: <Info className="w-4 h-4 text-gray-500" />,
    };
    return iconMap[notification.icon || 'system'];
  };

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-gray-900">Bildirimler</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-brand-red text-white text-[10px] font-bold rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllNotificationsRead}
              className="text-xs text-brand-red hover:text-red-700 font-medium transition-colors"
            >
              Tümünü okundu işaretle
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="max-h-96 overflow-y-auto">
        {sortedNotifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Henüz bildirim yok</p>
          </div>
        ) : (
          sortedNotifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => markNotificationRead(notification.id)}
              className={`flex items-start gap-3 p-4 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${
                !notification.read ? 'bg-brand-red/5' : ''
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {getIcon(notification)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm ${!notification.read ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                    {notification.title}
                  </p>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-brand-red rounded-full flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{notification.message}</p>
                <p className="text-[10px] text-gray-400 mt-1">{getTimeAgo(notification.timestamp)}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeNotification(notification.id);
                }}
                className="flex-shrink-0 p-1 hover:bg-gray-200 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              >
                <X className="w-3 h-3 text-gray-400" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
